import React from "react";
import { Link } from "react-router-dom";
import { Nav } from "@/components/layout/Nav";
import { Footer } from "@/components/layout/Footer";
import { PageSEO } from "@/components/seo/PageSEO";
import { SITE_URL } from "@/utils/seo";
import { Clock, AlertTriangle, CheckCircle, ArrowRight } from "lucide-react";

const SITE = SITE_URL;

const breadcrumbSchema = {
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: [
    { "@type": "ListItem", position: 1, name: "Home", item: `${SITE}/` },
    { "@type": "ListItem", position: 2, name: "Delivery Areas", item: `${SITE}/delivery` },
    { "@type": "ListItem", position: 3, name: "Walt Disney World Resorts", item: `${SITE}/delivery/walt-disney-world-resorts` },
  ],
};

const strollers = [
  { slug: "baby-jogger-city-mini-gt2-single", name: "Baby Jogger GT2 Single", price: 22 },
  { slug: "uppababy-vista-v2", name: "UPPAbaby VISTA V2", price: 26 },
  { slug: "baby-jogger-city-mini-gt2-double", name: "Baby Jogger GT2 Double", price: 34 },
  { slug: "bob-single-stroller", name: "BOB Single Stroller", price: 24 },
];

export default function DisneyDelivery() {
  return (
    <>
      <PageSEO
        title="Stroller Delivery to Walt Disney World Resorts | Enchanted Gear Rentals"
        description="Free stroller delivery to every Walt Disney World resort hotel. In-person lobby service — no bell-service drop-offs. One-hour delivery windows available 7 am – 10 pm daily."
        canonical={`${SITE}/delivery/walt-disney-world-resorts`}
        structuredData={breadcrumbSchema}
      />
      <Nav />
      <main className="min-h-screen" style={{ background: "#040A0F" }}>
        <section className="pt-28 pb-14 text-center" style={{ background: "linear-gradient(180deg, #060f18 0%, #040A0F 100%)" }}>
          <div className="container max-w-3xl">
            <nav aria-label="Breadcrumb" className="text-xs text-[#B9C8D5] mb-4">
              <Link to="/" className="hover:text-[#FFD166]">Home</Link>
              <span className="mx-2 opacity-40">/</span>
              <Link to="/delivery" className="hover:text-[#FFD166]">Delivery Areas</Link>
              <span className="mx-2 opacity-40">/</span>
              <span className="text-[#FFD166]">Walt Disney World Resorts</span>
            </nav>
            <h1 className="font-serif text-4xl md:text-5xl text-[#F6F1E4] mb-5">Stroller Delivery to Walt Disney World Resorts</h1>
            <p className="text-[#B9C8D5] text-lg max-w-2xl mx-auto">
              We deliver premium strollers to every Walt Disney World resort hotel. All deliveries are in-person — we meet you directly in the lobby so your gear is handed off safely and correctly.
            </p>
            <p className="mt-3 text-xs text-[#B9C8D5] italic">Enchanted Gear Rentals is not affiliated with The Walt Disney Company.</p>
          </div>
        </section>

        <div className="container max-w-3xl py-12 pb-20 space-y-14">
          {/* Important bell-services note */}
          <section className="rounded-xl border border-[#FFD166]/40 bg-[#0A1825] p-6">
            <div className="flex gap-3 items-start">
              <AlertTriangle className="w-5 h-5 text-[#FFD166] shrink-0 mt-0.5" />
              <div>
                <h2 className="font-semibold text-[#F6F1E4] mb-2">Important: No bell-services drop-offs</h2>
                <p className="text-sm text-[#B9C8D5] leading-relaxed">
                  Walt Disney World does not allow third-party rental vendors to leave equipment with bell services or concierge. Every delivery and pickup requires an in-person meeting between our team and the guest in the resort lobby. We will coordinate the exact meeting time and location with you before your rental begins.
                </p>
              </div>
            </div>
          </section>

          {/* How it works */}
          <section>
            <h2 className="font-serif text-2xl text-[#F6F1E4] mb-6">How Disney resort delivery works</h2>
            <div className="space-y-4">
              {[
                { step: "1", title: "Reserve online and select a delivery window", body: "Choose a 1-hour delivery window when booking — available any hour from 7 am to 10 pm. We will confirm your window before your trip." },
                { step: "2", title: "We arrive at your resort lobby", body: "Our team arrives at the resort lobby during your chosen window and contacts you to confirm the meeting location." },
                { step: "3", title: "In-person handoff", body: "We hand off the stroller directly to you, show you how to fold and use it, and answer any questions." },
                { step: "4", title: "Pickup at the end of your rental", body: "We meet you again at the resort lobby for pickup. Need a different time or location? Just contact us — we will work with your schedule." },
              ].map(({ step, title, body }) => (
                <div key={step} className="flex gap-4">
                  <div className="w-8 h-8 rounded-full bg-[#FFD166]/15 border border-[#FFD166]/30 flex items-center justify-center text-[#FFD166] font-bold text-sm shrink-0">{step}</div>
                  <div>
                    <div className="font-semibold text-[#F6F1E4]">{title}</div>
                    <div className="text-sm text-[#B9C8D5] mt-1">{body}</div>
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* Delivery windows */}
          <section>
            <h2 className="font-serif text-2xl text-[#F6F1E4] mb-5">Delivery windows</h2>
            <div className="grid sm:grid-cols-2 gap-4">
              {[
                { time: "7:00 am – 10:00 pm", note: "1-hour windows available throughout the day" },
                { time: "Any single hour", note: "Pick the exact hour that works for your schedule" },
                { time: "Early morning (7 am)", note: "Available at no extra charge" },
                { time: "After 10:00 pm", note: "Not available" },
              ].map(({ time, note }) => (
                <div key={time} className="flex items-center gap-3 rounded-lg bg-[#0A1825] border border-[#FFD166]/10 px-4 py-3">
                  <Clock className="w-4 h-4 text-[#FFD166] shrink-0" />
                  <div>
                    <div className="text-[#F6F1E4] font-medium text-sm">{time}</div>
                    <div className="text-xs text-[#B9C8D5]">{note}</div>
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* What to prepare */}
          <section>
            <h2 className="font-serif text-2xl text-[#F6F1E4] mb-5">What to have ready</h2>
            <ul className="space-y-2">
              {[
                "Your reservation confirmation number or name",
                "Your phone — we will text when we are on the way",
                "Be in the resort lobby during your chosen delivery window",
                "Allow 5–10 minutes for setup and handoff",
              ].map((item) => (
                <li key={item} className="flex items-start gap-2 text-sm text-[#F6F1E4]">
                  <CheckCircle className="w-4 h-4 text-[#FFD166] shrink-0 mt-0.5" /> {item}
                </li>
              ))}
            </ul>
          </section>

          {/* Recommended strollers */}
          <section>
            <h2 className="font-serif text-2xl text-[#F6F1E4] mb-5">Popular strollers for Disney World</h2>
            <div className="grid sm:grid-cols-2 gap-4">
              {strollers.map((s) => (
                <Link key={s.slug} to={`/strollers/${s.slug}`} className="group rounded-xl bg-[#0A1825] border border-[#FFD166]/10 hover:border-[#FFD166]/40 p-4 transition-all">
                  <div className="font-serif text-[#F6F1E4] group-hover:text-[#FFD166] transition-colors">{s.name}</div>
                  <div className="text-[#FFD166] text-sm mt-1">From ${s.price}/day</div>
                </Link>
              ))}
            </div>
            <div className="mt-4"><Link to="/strollers" className="text-sm text-[#FFD166] underline">View full stroller catalog</Link></div>
          </section>

          <div className="text-center">
            <Link to="/#trip-setup"><button className="btn-primary" data-testid="disney-delivery-cta">Reserve Your Stroller <ArrowRight className="inline w-4 h-4 ml-1" /></button></Link>
          </div>
        </div>
      </main>
      <Footer />
    </>
  );
}
